"use client";
import React, { useState, useEffect, useMemo } from "react";
import IncomeChart from "./../../IncomeChart/IncomeChart";
import NotificationPanel from "./../../Notifications/Notifications";
import NewsUpdates from "../../NewsCard/NewsCard";
import Calendar from "../../Calendar/Calendar";
import calendar_white from "../../assets/calendar_white.png";
import clients_white from "../../assets/clients_white.png";
import analytics_white from "../../assets/analytics_white.png";
import g from "../../assets/gif.gif";
import { REST_API_URL } from "../../api/config";
import { getPaymentCheckThunk } from "../../redux/slices/paymentSlice/asyncthunk";
import { useAppDispatch } from "../../hooks/redux/useRedux";

const API_URL = REST_API_URL;

const CURRENCY_SYMBOL_MAP = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  CAD: "C$",
  BRL: "R$",
  MXN: "$",
  AED: "د.إ",
  ARS: "$",
  ZAR: "R",
  ILS: "₪",
};

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

const getToken = () =>
  localStorage.getItem("accessToken") ||
  sessionStorage.getItem("accessToken") ||
  localStorage.getItem("token") ||
  sessionStorage.getItem("token");

const clearAuthStorage = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user_status");
  localStorage.removeItem("halper_remember_me");
  localStorage.removeItem("halper_saved_email");
  localStorage.removeItem("last_login_time");
  localStorage.removeItem("login_timezone");
  localStorage.removeItem("user_timezone");
  localStorage.removeItem("analyticsCurrency");
  localStorage.removeItem("analyticsCurrencyTimestamp");

  sessionStorage.removeItem("accessToken");
  sessionStorage.removeItem("refreshToken");
  sessionStorage.removeItem("user_status");
  sessionStorage.removeItem("last_login_time");
  sessionStorage.removeItem("login_timezone");
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  if (token) headers.Authorization = `Bearer ${token}`;
  headers["User-Timezone"] = getUserTimeZone();

  return headers;
};

const getCurrencySymbol = (code) => CURRENCY_SYMBOL_MAP[code] || code || "$";

const fetchUserCurrency = async (token) => {
  try {
    if (!token) {
      return { currency: "USD" };
    }

    const headers = buildHeaders(token);

    const res = await fetch(`${API_URL}/service/user-currency/`, {
      headers,
      cache: "no-store",
    });

    const responseData = await res.json().catch(() => ({}));

    if (!res.ok) {
      return { currency: "USD" };
    }

    const resolvedCurrency =
      responseData?.currency ||
      responseData?.user_currency ||
      responseData?.data?.currency ||
      "USD";

    return { currency: resolvedCurrency };
  } catch (error) {
    console.error("Error fetching user currency:", error);
    return { currency: "USD" };
  }
};

function Card({
  title,
  subtitle,
  growth,
  icon,
  showCurrencySymbol = false,
  currencySymbol = "$",
}) {
  return (
    <div className="rounded-lg bg-white shadow p-6 flex justify-between items-start">
      <div className="flex flex-col gap-5">
        <div className="h-8 w-8 flex items-center justify-center rounded-full bg-indigo-500">
          {showCurrencySymbol ? (
            <span className="text-white text-sm font-medium">
              {currencySymbol}
            </span>
          ) : (
            <img
              src={icon}
              alt={subtitle}
              className="max-w-[16px] max-h-[16px]"
            />
          )}
        </div>
        <div>
          <p className="text-xl font-semibold">{title}</p>
          <p className="text-sm text-neutral-500">{subtitle}</p>
        </div>
      </div>

      {growth && (
        <span
          className={`text-sm font-medium ${
            String(growth).startsWith("-") ? "text-red-500" : "text-green-500"
          }`}
        >
          {growth}
        </span>
      )}
    </div>
  );
}

export default function Home() {
  const dispatch = useAppDispatch();
  const [stats, setStats] = useState({
    totalRevenue: "0.00",
    revenueGrowth: "0%",
    appointmentsToday: "0",
    newCustomers: "0",
    currencySymbol: "$",
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const userTz = useMemo(() => getUserTimeZone(), []);

  const fetchAllData = async () => {
    dispatch(getPaymentCheckThunk());
    
    const token = getToken();

    if (!token) {
      setError("No access token found. Please sign in again.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const userCurrencyData = await fetchUserCurrency(token);
      const currencySymbol = getCurrencySymbol(userCurrencyData?.currency);

      const headers = buildHeaders(token);

      const analyticsRes = await fetch(
        `${API_URL}/analytics/analytics-veriables/`,
        {
          headers,
          cache: "no-store",
        },
      );

      if (analyticsRes.status === 401) {
        clearAuthStorage();
        setError("Session expired. Please sign in again.");
        return;
      }

      const analyticsData = await analyticsRes.json().catch(() => ({}));

      const notifRes = await fetch(
        `${API_URL}/notification/notifications-list/`,
        {
          headers,
          cache: "no-store",
        },
      );

      if (notifRes.status === 401) {
        clearAuthStorage();
        setError("Session expired. Please sign in again.");
        return;
      }

      const notifData = await notifRes.json().catch(() => ({}));

      const allAppointments = [
        ...(notifData.today || []),
        ...(notifData.earlier || []),
        ...(notifData.upcoming || []),
        ...(notifData.later || []),
      ];

      const today = new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        timeZone: userTz,
      });

      const todayAppointments = allAppointments.filter(
        (n) => n.appointment_date === today,
      );

      setStats({
        totalRevenue: analyticsData.clients_lifetime_value || "0.00",
        revenueGrowth:
          analyticsData.clients_lifetime_value_growth_percentage || "0%",
        appointmentsToday: String(todayAppointments.length),
        newCustomers: analyticsData.clients_count || "0",
        currencySymbol,
      });
    } catch (err) {
      console.error("❌ Error fetching dashboard data:", err);
      setError("Failed to load analytics or notifications data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();

    const handler = (e) => {
      if (e.detail?.add) {
        setStats((prev) => ({
          ...prev,
          appointmentsToday: String(
            Number(prev.appointmentsToday || 0) + Number(e.detail.add || 0),
          ),
        }));
      }

      setTimeout(fetchAllData, 1000);
    };

    window.addEventListener("refreshHomeStats", handler);
    return () => window.removeEventListener("refreshHomeStats", handler);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <img
          src={g}
          alt="Loading"
          style={{ maxWidth: "300px" }}
          className="loader-ed-image"
        />
      </div>
    );
  }

  if (error) {
    return (
      <main className="flex flex-1 items-center justify-center">
        <p className="text-red-500">{error}</p>
      </main>
    );
  }

  return (
    <main className="flex flex-1 flex-col overflow-auto p-4 gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card
          title={`${stats.currencySymbol}${stats.totalRevenue}`}
          subtitle="Total Revenue"
          growth={stats.revenueGrowth}
          showCurrencySymbol
          currencySymbol={stats.currencySymbol}
        />
        <Card
          title={stats.appointmentsToday}
          subtitle="Appointments Today"
          icon={calendar_white}
        />
        <Card
          title={stats.newCustomers}
          subtitle="New Customers"
          icon={clients_white}
        />
        <Card
          title={stats.revenueGrowth}
          subtitle="Revenue Growth"
          growth={stats.revenueGrowth}
          icon={analytics_white}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <div className="rounded-lg bg-white shadow h-full">
            <IncomeChart />
          </div>
        </div>
        <div className="h-[400px]">
          <div className="rounded-lg bg-white shadow h-full">
            <NewsUpdates />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 rounded-xl">
        <div className="xl:col-span-2 rounded-xl">
          <div
            className="bg-white rounded-xl shadow"
            style={{ borderRadius: "21px" }}
          >
            <Calendar />
          </div>
        </div>
        <div>
          <div
            className="bg-white rounded-xl shadow height-full-not"
            style={{ maxHeight: "780px", overflowY: "auto" }}
          >
            <NotificationPanel />
          </div>
        </div>
      </div>
    </main>
  );
}
